<template>
  <!--
    WHAT — Structured Analysis detail tab "Primitives". Edits the list of
      DataAttributes the analyst agent draws on. Each entry = a DA (da_id, picked
      from a searchable dropdown of all attributes), an alias, an agent-facing
      description, and an optional free-form params/overrides JSON blob.

    CONTRACT — hydrates from record.config.interventions[]. Emits patch({ config })
      where config is a spread of record.config with a rebuilt `interventions`
      array of { da_id, alias, description, ...parsedConfigJson }. Mutates the ONE
      shared `config` blob like the other Structured* tabs.

    NOTES — hydrating guard: DA list loads async in mounted(), `hydrating` clears
      on $nextTick. The per-row "Config (JSON)" textarea is PARSED before emit;
      on invalid JSON it sets intv._err and RETURNS WITHOUT EMITTING (no partial
      save). Local _open/_configStr/_err are UI-only and stripped from the patch.
      selfChrome: true — renders its own titled card; ArtifactDetail adds no chrome.
  -->
  <!-- Structured Analysis "Primitives" — the DataAttributes the agent draws on,
       each with an alias + description + optional params JSON. Self-chromed
       (renders its own cards). Patches config.interventions. -->
  <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
    <div class="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
      <span class="text-[13px] font-semibold text-gray-800">Primitives</span>
      <button class="text-[11px] font-semibold text-blue-600 hover:text-blue-800" @click="add">+ Add primitive</button>
    </div>
    <div class="p-5">
      <div v-if="!interventions.length" class="text-[11px] text-gray-400 italic py-2">No primitives yet.</div>

      <div v-for="(intv, idx) in interventions" :key="idx" class="border border-gray-200 rounded-lg p-3 mb-2 bg-gray-50/50">
        <div class="flex gap-2 items-start mb-2">
          <!-- DA selector -->
          <div :ref="(el) => { if (el) dropdownRefs[idx] = el; }" class="flex-1 relative">
            <button
              class="w-full text-left px-2 py-1.5 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400 flex items-center gap-2"
              @click="toggleDropdown(idx)"
            >
              <template v-if="intv.da_id && daLookup[intv.da_id]">
                <span class="text-[10px] font-mono text-gray-400 shrink-0">#{{ intv.da_id }}</span>
                <span class="text-xs text-gray-800 truncate">{{ daLookup[intv.da_id].name }}</span>
                <span class="text-[9px] px-1 py-0.5 rounded shrink-0" :class="roleClass(daLookup[intv.da_id].role)">{{ daLookup[intv.da_id].role || '—' }}</span>
              </template>
              <span v-else class="text-xs text-gray-400">Select primitive…</span>
            </button>
            <div v-if="openDropdown === idx" class="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              <input
                v-model="daSearch"
                type="text"
                placeholder="Search…"
                class="w-full px-2 py-1.5 text-xs border-b border-gray-200 focus:outline-none"
              />
              <div
                v-for="da in filteredDAs"
                :key="da.id"
                class="px-2 py-1.5 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                @click="selectDA(intv, da.id)"
              >
                <span class="text-[10px] font-mono text-gray-400 w-8 shrink-0">#{{ da.id }}</span>
                <span class="text-xs text-gray-800 truncate flex-1">{{ da.name }}</span>
                <span class="text-[9px] px-1 py-0.5 rounded shrink-0" :class="roleClass(da.role)">{{ da.role || '—' }}</span>
              </div>
              <div v-if="!filteredDAs.length" class="px-2 py-3 text-xs text-gray-400 text-center">No matches</div>
            </div>
          </div>
          <input v-model="intv.alias" type="text" placeholder="alias" class="w-28 text-xs px-2 py-1.5 border border-gray-200 rounded-lg font-mono focus:outline-none focus:border-blue-400" @input="emit" />
          <button class="w-6 h-6 mt-0.5 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" @click="remove(idx)">×</button>
        </div>
        <textarea v-model="intv.description" rows="2" placeholder="Description for the agent prompt…" class="w-full text-xs px-2 py-1 border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400 resize-none mb-1" @input="emit"></textarea>
        <button class="text-[10px] text-gray-400 hover:text-gray-600" @click="intv._open = !intv._open">
          {{ intv._open ? '▾' : '▸' }} Params &amp; overrides
        </button>
        <div v-if="intv._open" class="mt-2 pt-2 border-t border-dashed border-gray-200">
          <label class="block text-[10px] font-medium text-gray-500 mb-1">Config (JSON)</label>
          <textarea
            v-model="intv._configStr"
            rows="5"
            spellcheck="false"
            placeholder='{"params": {"metric_id": "125"}, "join": {"on": "ENTITY_ID = xwalk.ENTITY_ID"}}'
            class="w-full text-[11px] px-1.5 py-1 border border-gray-200 rounded-lg font-mono bg-white focus:outline-none focus:border-blue-400 resize-y"
            @input="emit"
          ></textarea>
          <p v-if="intv._err" class="text-[10px] text-red-500 mt-0.5">{{ intv._err }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios';

export default {
  name: 'StructuredPrimitivesSection',
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    return {
      interventions: this.hydrateInterventions(this.record.config || {}),
      allDAs: [],
      openDropdown: null,
      daSearch: '',
      dropdownRefs: {},
      hydrating: true,
    };
  },
  computed: {
    daLookup() {
      const m = {};
      this.allDAs.forEach((d) => { m[d.id] = d; });
      return m;
    },
    filteredDAs() {
      if (!this.daSearch) return this.allDAs;
      const q = this.daSearch.toLowerCase();
      return this.allDAs.filter((d) =>
        d.name.toLowerCase().includes(q) || String(d.id).includes(q) || (d.role || '').toLowerCase().includes(q));
    },
  },
  async mounted() {
    try {
      const { data } = await axios.get('api/impact_analytics/attributes/');
      this.allDAs = (data.results || data || []).sort((a, b) => a.name.localeCompare(b.name));
    } catch { /* leave empty */ }
    document.addEventListener('click', this.handleClickOutside);
    this.$nextTick(() => { this.hydrating = false; });
  },
  beforeUnmount() { document.removeEventListener('click', this.handleClickOutside); },
  methods: {
    hydrateInterventions(cfg) {
      return (cfg.interventions || []).map((intv) => {
        const { da_id, alias, description, ...rest } = intv;
        return {
          da_id: da_id ?? null,
          alias: alias || '',
          description: description || '',
          _open: false,
          _configStr: Object.keys(rest).length ? JSON.stringify(rest, null, 2) : '',
          _err: null,
        };
      });
    },
    add() {
      this.interventions.push({ da_id: null, alias: '', description: '', _open: false, _configStr: '', _err: null });
      this.emit();
    },
    remove(idx) { this.interventions.splice(idx, 1); this.emit(); },
    toggleDropdown(idx) {
      this.openDropdown = this.openDropdown === idx ? null : idx;
      this.daSearch = '';
    },
    selectDA(intv, daId) {
      intv.da_id = daId;
      this.openDropdown = null;
      this.emit();
    },
    roleClass(role) {
      const map = {
        population: 'bg-blue-50 text-blue-600', intervention: 'bg-purple-50 text-purple-600',
        denominator: 'bg-amber-50 text-amber-600', eagle_eye: 'bg-green-50 text-green-600',
        date_spine: 'bg-gray-100 text-gray-500', opportunity: 'bg-orange-50 text-orange-600',
      };
      return map[role] || 'bg-gray-100 text-gray-500';
    },
    handleClickOutside(e) {
      if (this.openDropdown !== null) {
        const ref = this.dropdownRefs[this.openDropdown];
        if (ref && !ref.contains(e.target)) this.openDropdown = null;
      }
    },
    emit() {
      if (this.hydrating) return;
      const cleaned = [];
      for (const intv of this.interventions) {
        const out = { da_id: intv.da_id, alias: intv.alias, description: intv.description };
        intv._err = null;
        if (intv._configStr && intv._configStr.trim()) {
          try { Object.assign(out, JSON.parse(intv._configStr)); }
          catch (e) { intv._err = `Invalid JSON: ${e.message}`; return; } // don't emit while invalid
        }
        cleaned.push(out);
      }
      const config = { ...(this.record.config || {}), interventions: cleaned };
      this.$emit('patch', { config });
    },
  },
};
</script>
